// The in-call email check as a pure state machine, so the sequencing is testable
// (this workspace has no @testing-library/react).

// Rides out the pauses in someone reading an address aloud. Enter skips it.
export const EMAIL_CHECK_DEBOUNCE_MS = 1200;

export type EmailVerdict =
  | { kind: 'valid'; free: boolean }
  | { kind: 'unconfirmed'; free: boolean; risky: boolean; catchAll: boolean }
  | { kind: 'invalid' }
  | { kind: 'unchecked' };

export type EmailCheckResponse =
  | { ok: true; verdict: EmailVerdict }
  | { ok: false; reason: string; ref?: string | null; message?: string | null };

export type EmailCheckTone =
  | 'idle'
  | 'checking'
  | 'valid'
  | 'unconfirmed'
  | 'invalid'
  | 'unchecked'
  | 'error';

// Flat, not a union: no field here excludes another.
export interface EmailCheckView {
  tone: EmailCheckTone;
  badge: string | null;
  // What to do about it.
  note: string | null;
  // The free-provider line, which rides alongside a verdict rather than replacing it.
  nudge: string | null;
  retry: boolean;
  ref: string | null;
  // The failure on one line, for the clipboard.
  copy: string | null;
}

export interface EmailCheckState {
  input: string;
  // The last address answered, so an unchanged string is not asked twice.
  lastChecked: string | null;
  // In flight, and the token its answer must carry.
  pending: string | null;
  seq: number;
  view: EmailCheckView;
}

export type EmailCheckEvent =
  | { t: 'typed'; value: string }
  | { t: 'debounce_elapsed' }
  | { t: 'submit' }
  | { t: 'retry' }
  | { t: 'response'; seq: number; res: EmailCheckResponse; at: Date }
  | { t: 'call_changed' };

export type EmailCheckEffect =
  | { t: 'request'; email: string; seq: number }
  | { t: 'cancel' }
  | null;

const FREE_NUDGE = 'Personal address — ask if they have a work email.';
const READ_BACK = 'Worth reading it back to them.';

const IDLE: EmailCheckView = {
  tone: 'idle',
  badge: null,
  note: null,
  nudge: null,
  retry: false,
  ref: null,
  copy: null,
};

const CHECKING: EmailCheckView = { ...IDLE, tone: 'checking' };

export function initial(): EmailCheckState {
  return { input: '', lastChecked: null, pending: null, seq: 0, view: IDLE };
}

// Null when the string is worth a check. A dotted domain, so a half-typed address
// costs no request.
export function emailFormatIssue(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return 'empty';
  if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(value)) return 'malformed';
  return null;
}

function domainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase();
}

const FAILURE_NOTE: Record<string, string> = {
  timeout: 'The checker didn’t answer in time.',
  upstream: 'The checker couldn’t be reached.',
  unconfigured: 'Email checking isn’t set up yet.',
  rate_limited: 'Too many checks just now — give it a moment.',
  invalid_format: 'That doesn’t look like an email address.',
};

function failureView(
  res: Extract<EmailCheckResponse, { ok: false }>,
  email: string,
  at: Date,
): EmailCheckView {
  const note = res.message ?? FAILURE_NOTE[res.reason] ?? 'The check didn’t complete.';
  const ref = res.ref ?? null;
  // Domain only, never the local part — this gets pasted into chat.
  const copy = [
    `Email check failed — ${res.reason}`,
    [ref ? `ref ${ref}` : null, `${at.toISOString().slice(11, 16)} UTC`, domainOf(email)]
      .filter(Boolean)
      .join(' · '),
  ].join('\n');
  return { tone: 'error', badge: 'Email check failed', note, nudge: null, retry: true, ref, copy };
}

export function viewFromResponse(res: EmailCheckResponse, email: string, at: Date): EmailCheckView {
  if (!res.ok) return failureView(res, email, at);

  switch (res.verdict.kind) {
    case 'valid':
      return {
        ...IDLE,
        tone: 'valid',
        badge: 'Valid',
        nudge: res.verdict.free ? FREE_NUDGE : null,
      };
    case 'unconfirmed':
      return {
        ...IDLE,
        tone: 'unconfirmed',
        badge: 'Can’t confirm',
        note: READ_BACK,
        nudge: res.verdict.free ? FREE_NUDGE : null,
      };
    // No nudge: asking for another address is the wrong step when this one is wrong.
    case 'invalid':
      return { ...IDLE, tone: 'invalid', badge: 'Not deliverable' };
    case 'unchecked':
      return {
        ...IDLE,
        tone: 'unchecked',
        badge: 'Couldn’t check',
        note: 'The checker is having trouble.',
        retry: true,
      };
  }
}

// `force` is Retry: the request the guards below would otherwise refuse.
function fire(
  state: EmailCheckState,
  force: boolean,
): { state: EmailCheckState; effect: EmailCheckEffect } {
  const email = state.input.trim();
  if (emailFormatIssue(email)) return { state, effect: null };
  // Already answered, or already in flight — the second is Enter racing its own
  // debounce.
  if (!force && (email === state.lastChecked || email === state.pending)) {
    return { state, effect: null };
  }

  const seq = state.seq + 1;
  return {
    state: { ...state, seq, pending: email, view: CHECKING },
    effect: { t: 'request', email, seq },
  };
}

export function reduce(
  state: EmailCheckState,
  event: EmailCheckEvent,
): { state: EmailCheckState; effect: EmailCheckEffect } {
  switch (event.t) {
    case 'typed': {
      if (event.value === state.input) return { state, effect: null };
      // Drop the old string's verdict, the record that it had one (else editing away
      // and back is never re-checked), and any answer still coming for it.
      return {
        state: { ...state, input: event.value, lastChecked: null, pending: null, view: IDLE },
        effect: { t: 'cancel' },
      };
    }

    case 'debounce_elapsed':
    case 'submit':
      return fire(state, false);

    case 'retry':
      return fire(state, true);

    case 'response': {
      // An aborted request still resolves, so both guards are needed: nothing in
      // flight, or something later in flight than this answer.
      if (state.pending === null || event.seq !== state.seq) return { state, effect: null };
      const email = state.pending;
      return {
        state: {
          ...state,
          pending: null,
          lastChecked: email,
          view: viewFromResponse(event.res, email, event.at),
        },
        effect: null,
      };
    }

    // A new person on the line. The counter survives, so the dead call's answer
    // cannot wear the new call's token.
    case 'call_changed':
      return { state: { ...initial(), seq: state.seq }, effect: { t: 'cancel' } };
  }
}
